import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { usePagination } from '../hooks/usePagination';
import { useMedicationOperations } from '../hooks/useMedicationOperations';

type TProps = {
  children: ReactNode;
};

type TContext = {
  selectedMed: TMed | null;
  isPanelOpen: boolean;
  limit: number;
  offset: number;
  medsList: TMedsList;
  drugDetail: TMed | null;
  loadingMed: boolean;
  alertMsgs: Partial<Record<string, string[]>>;
  setDrugDetail: Dispatch<SetStateAction<TMed | null>>;
  setAlertMessages: Dispatch<SetStateAction<Partial<Record<string, string[]>>>>;
  setOffset: Dispatch<SetStateAction<number>>;
  setLimit: Dispatch<SetStateAction<number>>;
  setIsPanelOpen: Dispatch<SetStateAction<boolean>>;
  setSelectedMed: Dispatch<SetStateAction<TMed | null>>;
  getActiveIngridients: () => Promise<any>;
  getDosageUnits: () => Promise<any>;
  editDrug: (id: string, params: TKiroDetail) => Promise<TMed | null>;
  getDrugList: (useInKiro?: boolean, search?: string) => void;
  getDrugDetail: (id: string) => void;
};

export type TKiroDetail = {
  use_in_kiro:boolean;
  active_ingredient: string | null;
  concentration: string | null;
  concentration_unit: string | null;
  density: string | null;
  vial_dose: string | null;
  vial_unit: string | null;
  id?: string | null;
  name?: string | null;
  volume?: string | null;
  is_final_container?: boolean | null;
  final_container_id: number | null;
  cycle_type: string | null;
};

export type TMed = {
  id: string;
  damumed_id?: number;
  drug_id?: number;
  category?: {
    his_id: number;
    name: number;
  };
  kiro_details: TKiroDetail;
  route: string;
  his_id: number;
  active_ingredient?: string;
  brand_name?: string;
  brand_name_ru?: string;
  brand_name_kz?: string;
  dose_and_unit?: string;
  concentration?: string | number | null;
  source?: string;
  dosage_value: number;
  damumed_concentration?: string | null;
  dosage_unit: string;
  manufacture: string;
  country: string;
  his_system?: string;
  has_options?: [];
  use_in_kiro?: boolean;
};

export type TMedsList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: TMed[];
  last_updated_stamp?: Date;
};

const MedsContext = createContext<TContext>({} as TContext);

export const MedsProvider = ({ children }: TProps) => {
  const [selectedMed, setSelectedMed] = useState<TMed | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [alertMsgs, setAlertMessages] = useState<Partial<Record<string, string[]>>>({});
  const { limit, offset, setLimit, setOffset } = usePagination(10);
  const {
    medsList,
    drugDetail,
    loadingMed,
    setDrugDetail,
    getDrugList: fetchDrugList,
    getDrugDetail: fetchDrugDetail,
    getActiveIngredients: fetchActiveIngredients,
    getDosageUnits: fetchDosageUnits,
    editDrug: editDrugOperation,
  } = useMedicationOperations();

  const getDrugList = useCallback(async (useInKiro?: boolean, search?: string) => {
    await fetchDrugList(useInKiro, search, limit, offset, setAlertMessages);
  }, [fetchDrugList, limit, offset]);

  const getDrugDetail = useCallback(async (id: string) => {
    await fetchDrugDetail(id, setAlertMessages);
  }, [fetchDrugDetail]);

  const getActiveIngridients = useCallback(async () => {
    return await fetchActiveIngredients(setAlertMessages);
  }, [fetchActiveIngredients]);

  const getDosageUnits = useCallback(async () => {
    return await fetchDosageUnits(setAlertMessages);
  }, [fetchDosageUnits]);

  const editDrug = useCallback(async (id: string, params: TKiroDetail): Promise<TMed | null> => {
    return await editDrugOperation(id, params, setAlertMessages);
  }, [editDrugOperation]);

  const contextValue = useMemo(
    () => ({
      medsList,
      selectedMed,
      isPanelOpen,
      limit,
      offset,
      drugDetail,
      alertMsgs,
      loadingMed,
      setDrugDetail,
      setAlertMessages,
      editDrug,
      getDosageUnits,
      getActiveIngridients,
      getDrugDetail,
      getDrugList,
      setOffset,
      setLimit,
      setSelectedMed,
      setIsPanelOpen,
    }),
    [
      medsList,
      selectedMed,
      isPanelOpen,
      limit,
      offset,
      drugDetail,
      alertMsgs,
      loadingMed,
      editDrug,
      getDosageUnits,
      getActiveIngridients,
      getDrugDetail,
      getDrugList,
    ]
  );

  return <MedsContext.Provider value={contextValue}>{children}</MedsContext.Provider>;
};

export const useMeds = () => {
  return useContext(MedsContext);
};
